import { Injectable } from '@angular/core';
import { fetchOneEntry, fetchEntries, type BuilderContent } from '@builder.io/sdk-angular';
import { environment } from '../../environments/environment';

/**
 * Builder.io Service
 * 
 * Follows Builder.io Gen2 SDK best practices:
 * - Centralizes all Builder.io API interactions using Gen2 SDK methods
 * - Uses the 'blog' model for Blog Article content
 * - Uses the 'symbol' model for reusable content sections (headers, footers)
 * - Provides type-safe methods to fetch content from different models
 * - Handles errors gracefully with proper logging
 */
@Injectable({
  providedIn: 'root'
})
export class BuilderService {
  private readonly apiKey = environment.builderApiKey;

  /**
   * Fetch a blog article by slug from the 'blog' model (Gen2 SDK)
   * Tries multiple slug variations to handle different formats (with/without leading slash)
   * @param slug - The blog article slug
   * @returns Promise<BuilderContent | null>
   */
  async getBlogBySlug(slug: string): Promise<BuilderContent | null> {
    try {
      // Try exact match first
      let result = await this.tryFetchBlog(slug);
      
      if (result) {
        return result;
      }
      
      // Try with leading slash
      const slugWithLeadingSlash = `/${slug}`;
      result = await this.tryFetchBlog(slugWithLeadingSlash);
      
      if (result) {
        return result;
      }
      
      // Try without leading slash if original had one
      if (slug.startsWith('/')) {
        const slugWithoutLeadingSlash = slug.substring(1);
        result = await this.tryFetchBlog(slugWithoutLeadingSlash);
        
        if (result) {
          return result;
        }
      }
      
      return null;
    } catch (error) {
      console.error('BuilderService: Error fetching blog article:', error);
      return null;
    }
  }

  /**
   * Helper method to try fetching a blog with a specific slug
   */
  private async tryFetchBlog(slug: string): Promise<BuilderContent | null> {
    try {
      return await fetchOneEntry({
        model: 'blog',
        apiKey: this.apiKey,
        query: {
          'data.slug': slug
        },
        options: {
          includeRefs: true
        }
      });
    } catch (error) {
      return null;
    }
  }

  /**
   * Fetch a page by URL path from the 'page' model (Gen2 SDK)
   * @param urlPath - The page URL path
   * @returns Promise<BuilderContent | null>
   */
  async getPageByUrl(urlPath: string): Promise<BuilderContent | null> {
    try {
      return await this.tryFetchPage(urlPath);
    } catch (error) {
      console.error('BuilderService: Error fetching page:', error);
      return null;
    }
  }

  /**
   * Fetch a page with audience context - handles homepage vs subpage URL patterns
   * 
   * Homepage (childPath empty): Tries multiple URL variations
   *   1. /highschool
   *   2. /
   * 
   * Subpages (childPath not empty): Tries just the child path first
   *   1. /page/product-landing-page-new (just child path)
   *   2. /highschool/page/product-landing-page-new (with audience)
   * 
   * @param childPath - The path after /:audience/ (empty for homepage)
   * @param audience - The audience (highschool or highered)
   * @returns Promise<BuilderContent | null>
   */
  async getPageByUrlWithAudience(childPath: string, audience: string): Promise<BuilderContent | null> {
    try {
      let result: BuilderContent | null = null;

      if (!childPath) {
        // HOMEPAGE: Try multiple URL patterns
        // 1. Just audience path
        result = await this.tryFetchPage(`/${audience}`);
        if (result) return result;

        // 2. Root path
        result = await this.tryFetchPage('/');
        if (result) return result;
      } else {
        // SUBPAGE: Try multiple URL patterns
        // 1. Just the child path (most likely in Builder.io)
        result = await this.tryFetchPage(`/${childPath}`);
        if (result) return result;

        // 2. With audience prefix
        result = await this.tryFetchPage(`/${audience}/${childPath}`);
        if (result) return result;
      }

      return null;
    } catch (error) {
      console.error('BuilderService: Error fetching page with audience:', error);
      return null;
    }
  }

  /**
   * Helper method to try fetching a page with a specific URL path
   */
  private async tryFetchPage(urlPath: string): Promise<BuilderContent | null> {
    try {
      return await fetchOneEntry({
        model: 'page',
        apiKey: this.apiKey,
        userAttributes: {
          urlPath: urlPath
        }
      });
    } catch (error) {
      return null;
    }
  }

  /**
   * Generic method to fetch a single entry from any Builder.io model (Gen2 SDK)
   * @param modelName - The Builder.io model name (e.g., 'blog', 'page', 'symbol')
   * @param options - Query options and filters
   * @returns Promise<BuilderContent | null>
   */
  async getContent(modelName: string, options?: any): Promise<BuilderContent | null> {
    try {
      return await fetchOneEntry({
        model: modelName,
        apiKey: this.apiKey,
        ...options
      });
    } catch (error) {
      console.error(`BuilderService: Error fetching ${modelName} content:`, error);
      return null;
    }
  }

  /**
   * Fetch multiple entries from a Builder.io model (Gen2 SDK)
   * Used for symbols, blog-promo-sections, and other collection-based content
   * @param modelName - The Builder.io model name
   * @param options - Query options and filters
   * @returns Promise<BuilderContent[]>
   */
  async getMultipleContent(modelName: string, options?: any): Promise<BuilderContent[]> {
    try {
      const results = await fetchEntries({
        model: modelName,
        apiKey: this.apiKey,
        ...options
      });

      return results || [];
    } catch (error) {
      console.error(`BuilderService: Error fetching multiple ${modelName} entries:`, error);
      return [];
    }
  }
}

